import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Iconify from 'src/components/Iconify';
import ListActionBar from 'src/components/ListActionBar';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import StatusChip from 'src/components/StatusChip';
import TableContainer from 'src/components/Table/TableContainer';
import TableHeadTypography from 'src/components/TableHeadTypography';
import HighlightedText from 'src/components/HighlightedText';
import { TableCellTypography } from 'src/components/GenericTableCell';
import { pagesText } from 'src/constants/pagesText';
import { PORTAL_ROUTE } from 'src/constants/routes';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { formatDate } from 'src/utils/formatTime';
import {
  APPLICATION_TRUST_STATUS,
  getApplicationTrustStatusColor,
  getApplicationTrustStatusLabel,
} from 'src/utils/enums/applicationTrustStatus';
import { WDAC_APP_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/Win32Apps/constants';
import { DEVICE_WIN32_APPS_COLUMNS, DEVICE_WIN32_APPS_COLUMN_GROUPS } from './constants';

const GROUP_ROW_HEIGHT = 30;
const GROUP_DIVIDER_SX = { borderRight: '2px solid #2E3236' };

const ELLIPSIS_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  minWidth: 0,
};

const STATUS_FILTER_OPTIONS = [
  {
    key: 'applicationTrustStatuses',
    type: 'checkbox',
    label: 'Include',
    options: Object.values(APPLICATION_TRUST_STATUS).map(value => ({
      value,
      label: getApplicationTrustStatusLabel(value),
    })),
  },
];

const ColumnHeader = ({ column, sort, onSort, mediumScreen, largeScreen, smallScreen }) => {
  const sortActive = column.sortable && sort.column === column.sortKey;

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onClick={column.sortable ? () => onSort(column) : undefined}
      sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sortActive}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {column.label}
      </TableHeadTypography>
      {column.sortable && (
        <Iconify
          icon={
            sortActive
              ? sort.direction === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sortActive ? '#7DFF92' : '#9CA3AF'}
        />
      )}
    </Stack>
  );
};

const AppResultTable = ({
  isLoading,
  results,
  sentryRef,
  isFetchingNextPage,
  searchValue,
  onSearchChange,
  listFilters,
  onListFiltersChange,
  sort,
  onSortChange,
  totalCount,
}) => {
  const navigate = useNavigate();
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const handleSort = useCallback(
    column => {
      if (!column.sortable) {
        return;
      }

      onSortChange(
        sort.column === column.sortKey
          ? { column: column.sortKey, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : { column: column.sortKey, direction: 'desc' },
      );
    },
    [onSortChange, sort],
  );

  const onFilterChange = useCallback(
    nextFilters => {
      onListFiltersChange({
        applicationTrustStatuses: (nextFilters.applicationTrustStatuses || [])
          .map(value => String(value))
          .filter(Boolean),
      });
    },
    [onListFiltersChange],
  );

  const openAppDetails = useCallback(
    row => {
      sessionStorage.setItem(WDAC_APP_PREVIOUS_PATH, window.location.pathname);
      navigate(`${PORTAL_ROUTE}/wdac-apps/${row.id}/telemetry`);
    },
    [navigate],
  );

  const trimmedSearch = (searchValue || '').trim();

  const renderCell = useCallback(
    (column, row) => {
      switch (column.key) {
        case 'lastSeen':
          return (
            <TableCell key={`${row.id}-lastSeen`}>
              <TableCellTypography>
                {row.lastSeen ? formatDate(row.lastSeen) : '-'}
              </TableCellTypography>
            </TableCell>
          );
        case 'status': {
          const statusColor = getApplicationTrustStatusColor(row.status);
          return (
            <TableCell key={`${row.id}-status`}>
              <StatusChip
                label={getApplicationTrustStatusLabel(row.status)}
                color={statusColor}
                backgroundColor={`${statusColor}1F`}
              />
            </TableCell>
          );
        }
        default:
          return (
            <TableCell key={`${row.id}-${column.key}`} sx={{ minWidth: 0 }}>
              <TableCellTypography sx={ELLIPSIS_SX}>
                <HighlightedText text={row[column.key]} searchValue={trimmedSearch} fallback="-" />
              </TableCellTypography>
            </TableCell>
          );
      }
    },
    [trimmedSearch],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={onSearchChange}
        defaultSearch={searchValue}
        searchPlaceholder="Search apps..."
        filters={listFilters}
        onFilterChange={onFilterChange}
        filterOptions={STATUS_FILTER_OPTIONS}
        filterChips
        limitChips={5}
        filterButtonCount={totalCount}
      />

      <TableContainer roundedTop styles={{ maxHeight: 'calc(100vh - 372px)' }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {DEVICE_WIN32_APPS_COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
            <TableRow>
              {DEVICE_WIN32_APPS_COLUMN_GROUPS.map((group, index) => (
                <TableCell
                  key={group.title || `group-${index}`}
                  colSpan={group.span}
                  align="center"
                  sx={{
                    top: 0,
                    height: GROUP_ROW_HEIGHT,
                    p: '0 8px',
                    ...(index < DEVICE_WIN32_APPS_COLUMN_GROUPS.length - 1
                      ? GROUP_DIVIDER_SX
                      : null),
                  }}>
                  <TableHeadTypography
                    mediumScreen={mediumScreen}
                    largeScreen={largeScreen}
                    smallScreen={smallScreen}>
                    {group.title}
                  </TableHeadTypography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {DEVICE_WIN32_APPS_COLUMNS.map(column => (
                <TableCell
                  key={column.key}
                  sx={{
                    top: GROUP_ROW_HEIGHT,
                    zIndex: 3,
                    p: '8px 16px',
                    verticalAlign: 'middle',
                    ...(column.groupEnd ? GROUP_DIVIDER_SX : null),
                  }}>
                  <ColumnHeader
                    column={column}
                    sort={sort}
                    onSort={handleSort}
                    mediumScreen={mediumScreen}
                    largeScreen={largeScreen}
                    smallScreen={smallScreen}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {isLoading ? (
              <LoadingRows columnCount={DEVICE_WIN32_APPS_COLUMNS.length} rowCount={5} />
            ) : (
              <>
                {(results || []).map((row, index) => (
                  <TableRow
                    key={row.id}
                    ref={index === results.length - 1 ? sentryRef : null}
                    onClick={() => openAppDetails(row)}
                    sx={{ cursor: 'pointer' }}>
                    {DEVICE_WIN32_APPS_COLUMNS.map(column => renderCell(column, row))}
                  </TableRow>
                ))}
                {isFetchingNextPage && (
                  <LoadingRows columnCount={DEVICE_WIN32_APPS_COLUMNS.length} rowCount={3} />
                )}
                {!isFetchingNextPage && !(results || []).length && (
                  <NoDataCell colSpan={DEVICE_WIN32_APPS_COLUMNS.length}>
                    {pagesText.noDataFound}
                  </NoDataCell>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default memo(AppResultTable);
